/**
 * Routes Configuration
 * @apal/web-apal
 *
 * Routing for the standalone APAL portal application.
 * Routes are at root level (no /apal prefix).
 */

import * as React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet, useNavigate } from "react-router-dom";

import { AuthGuard, GuestGuard } from "@apal/shared-auth";
import { ErrorBoundary } from "@apal/shared-ui";
import { TierGuard } from "./guards/tier-guard";
import { AccessDenied } from "./components/access-denied";
import { paths } from "./config/paths";
import { defaultSettings } from "./config/dashboard";
import { SettingsProvider } from "./components/core/settings/settings-context";
import { VerticalLayout } from "./components/dashboard/layout/vertical/vertical-layout";

// Lazy-loaded pages
const LoginPage = React.lazy(() => import("./pages/login"));
const RegisterPage = React.lazy(() => import("./pages/register"));
const ForgotPasswordPage = React.lazy(() => import("./pages/forgot-password"));
const ResetPasswordPage = React.lazy(() => import("./pages/reset-password"));
const DashboardIndexPage = React.lazy(() => import("./pages/dashboard/index"));
const GoalsListPage = React.lazy(() => import("./pages/dashboard/goals/index"));
const PersonaSelectionPage = React.lazy(() => import("./pages/dashboard/goals/create/index"));
const GoalCreateManualPage = React.lazy(() => import("./pages/dashboard/goals/create/manual"));
const AIChatPage = React.lazy(() => import("./pages/dashboard/goals/create/ai/[personaId]"));
const GoalDetailsPage = React.lazy(() => import("./pages/dashboard/goals/[id]"));
const GoalEditPage = React.lazy(() => import("./pages/dashboard/goals/[id]/edit"));
const GoalAssignmentsPage = React.lazy(() => import("./pages/dashboard/goals/assignments"));
const TeamGoalsPage = React.lazy(() => import("./pages/dashboard/goals/team"));
const SharedWithMePage = React.lazy(() => import("./pages/dashboard/goals/shared-with-me"));
const NotFoundPage = React.lazy(() => import("./pages/not-found"));
const NotAuthorizedPage = React.lazy(() => import("./pages/errors/not-authorized"));
const TeamDashboardPage = React.lazy(() => import("./pages/dashboard/team"));
const CompanyDashboardPage = React.lazy(() => import("./pages/dashboard/company"));
const AnalyticsDashboardPage = React.lazy(() => import("./pages/dashboard/analytics"));
const AccountIndexPage = React.lazy(() => import("./pages/dashboard/account/index"));
const AccountProfilePage = React.lazy(() => import("./pages/dashboard/account/profile"));
const AccountSecurityPage = React.lazy(() => import("./pages/dashboard/account/security"));
const AccountNotificationsPage = React.lazy(() => import("./pages/dashboard/account/notifications"));
const AccountSessionsPage = React.lazy(() => import("./pages/dashboard/account/sessions"));

// Assessment pages (REQ-009: 12V Question Engine)
const AssessmentWizardPage = React.lazy(() => import("./pages/dashboard/goals/[id]/assessment"));
const AssessmentCompletePage = React.lazy(() => import("./pages/dashboard/goals/[id]/assessment/complete"));
const AssessmentHistoryPage = React.lazy(() => import("./pages/dashboard/goals/[id]/assessments"));

// Score pages (REQ-010: BAS/OSS Calculation)
const GoalScoresPage = React.lazy(() => import("./pages/dashboard/goals/[id]/scores"));
const BehavioralDashboardPage = React.lazy(() => import("./pages/dashboard/behavioral"));

// Scheduling pages (REQ-011: Assessment Scheduling)
const GoalSchedulingPage = React.lazy(() => import("./pages/dashboard/goals/[id]/scheduling"));

// Employee pages (REQ-023: Employee CSV Import)
const EmployeesIndexPage = React.lazy(() => import("./pages/dashboard/employees/index"));
const EmployeeImportPage = React.lazy(() => import("./pages/dashboard/employees/import"));
const EmployeeImportHistoryPage = React.lazy(() => import("./pages/dashboard/employees/import/history"));

// Help pages (REQ-043: Help Articles Portal)
const HelpIndexPage = React.lazy(() => import("./pages/dashboard/help/index"));
const HelpArticlePage = React.lazy(() => import("./pages/dashboard/help/article"));
const HelpContactPage = React.lazy(() => import("./pages/dashboard/help/contact"));

// Loading fallback
function LoadingFallback() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      Loading...
    </div>
  );
}

// Dashboard layout wrapper with auth protection and sidebar navigation
function DashboardLayout() {
  const navigate = useNavigate();

  const handleUnauthenticated = React.useCallback(() => {
    navigate(paths.login, { replace: true });
  }, [navigate]);

  const handleUnauthorized = React.useCallback(() => {
    navigate(paths.errors.notAuthorized, { replace: true });
  }, [navigate]);

  return (
    <AuthGuard
      currentPortal="APAL"
      onUnauthenticated={handleUnauthenticated}
      onUnauthorized={handleUnauthorized}
      loadingComponent={<LoadingFallback />}
    >
      <SettingsProvider settings={defaultSettings}>
        <VerticalLayout>
          <ErrorBoundary sectionName="Dashboard">
            <React.Suspense fallback={<LoadingFallback />}>
              <Outlet />
            </React.Suspense>
          </ErrorBoundary>
        </VerticalLayout>
      </SettingsProvider>
    </AuthGuard>
  );
}

// Guest layout wrapper (for login/register)
function GuestLayout() {
  const navigate = useNavigate();

  const handleAuthenticated = React.useCallback(() => {
    navigate(paths.dashboard.index, { replace: true });
  }, [navigate]);

  return (
    <GuestGuard
      onAuthenticated={handleAuthenticated}
      loadingComponent={<LoadingFallback />}
    >
      <React.Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </React.Suspense>
    </GuestGuard>
  );
}

export const routes: RouteObject[] = [
  // Root redirect to login
  {
    index: true,
    element: <Navigate to={paths.login} replace />,
  },

  // Guest routes (login, register, etc.)
  {
    element: <GuestLayout />,
    children: [
      {
        path: "login",
        element: <LoginPage />,
      },
      {
        path: "register",
        element: <RegisterPage />,
      },
      {
        path: "forgot-password",
        element: <ForgotPasswordPage />,
      },
      {
        path: "reset-password",
        element: <ResetPasswordPage />,
      },
    ],
  },

  // Dashboard routes (protected)
  {
    path: "dashboard",
    element: <DashboardLayout />,
    children: [
      {
        index: true,
        element: <DashboardIndexPage />,
      },
      {
        path: "goals",
        children: [
          {
            index: true,
            element: <GoalsListPage />,
          },
          {
            path: "create",
            children: [
              {
                index: true,
                element: <PersonaSelectionPage />,
              },
              {
                path: "manual",
                element: <GoalCreateManualPage />,
              },
              {
                path: "ai/:personaId",
                element: <AIChatPage />,
              },
            ],
          },
          // REQ-R2-FE-1: `/dashboard/goals/assessments` is exercised by QA's
          // suite but was never a route. The literal segment was captured by
          // `:id` below, the detail page requested `/api/v1/goals/assessments`,
          // and the error path bounced the user to `/dashboard`.
          //
          // A cross-goal assessment index has never been designed. Redirecting
          // to the goals list satisfies the intent ("reach assessments") using
          // the surface that already lists goals with correctly gated Assess
          // controls. Building a real index is a separate feature REQ.
          //
          // NOTE: this MUST stay above `:id` for readability; React Router
          // ranks static segments above dynamic ones regardless of order, and
          // `routes-assessments.test.ts` pins that resolution.
          {
            path: "assessments",
            element: <Navigate to={paths.dashboard.goals.index} replace />,
          },
          {
            path: ":id",
            element: <GoalDetailsPage />,
          },
          {
            path: ":id/edit",
            element: <GoalEditPage />,
          },
          // Assessment routes (REQ-009: 12V Question Engine)
          {
            path: ":id/assessment",
            element: <AssessmentWizardPage />,
          },
          {
            path: ":id/assessment/complete",
            element: <AssessmentCompletePage />,
          },
          {
            path: ":id/assessments",
            element: <AssessmentHistoryPage />,
          },
          // Score routes (REQ-010: BAS/OSS Calculation)
          {
            path: ":id/scores",
            element: <GoalScoresPage />,
          },
          // Scheduling routes (REQ-011: Assessment Scheduling)
          {
            path: ":id/scheduling",
            element: <GoalSchedulingPage />,
          },
          {
            path: "assignments",
            element: <GoalAssignmentsPage />,
          },
          {
            path: "team",
            element: <TeamGoalsPage />,
          },
          {
            path: "shared-with-me",
            element: <SharedWithMePage />,
          },
        ],
      },
      // Employee Management (REQ-023: CSV Import) - Requires T3+ tier
      {
        path: "employees",
        children: [
          {
            index: true,
            element: (
              <TierGuard minTier={3} fallback={<AccessDenied message="Manager or higher access is required to manage employees." />}>
                <EmployeesIndexPage />
              </TierGuard>
            ),
          },
          {
            path: "import",
            children: [
              {
                index: true,
                element: (
                  <TierGuard minTier={3} fallback={<AccessDenied message="Manager or higher access is required to import employees." />}>
                    <EmployeeImportPage />
                  </TierGuard>
                ),
              },
              {
                path: "history",
                element: (
                  <TierGuard minTier={3} fallback={<AccessDenied message="Manager or higher access is required to view import history." />}>
                    <EmployeeImportHistoryPage />
                  </TierGuard>
                ),
              },
            ],
          },
        ],
      },
      // Team Dashboard (REQ-008 Phase 4) - Requires T2+ tier
      {
        path: "team",
        element: (
          <TierGuard minTier={2} fallback={<AccessDenied message="Team Lead or higher access is required to view the Team Dashboard." />}>
            <TeamDashboardPage />
          </TierGuard>
        ),
      },
      // Company Dashboard (REQ-008 Phase 5) - Requires T4+ tier
      {
        path: "company",
        element: (
          <TierGuard minTier={4} fallback={<AccessDenied message="Executive or higher access is required to view the Company Dashboard." />}>
            <CompanyDashboardPage />
          </TierGuard>
        ),
      },
      // Analytics Dashboard (REQ-008 Phase 6) - Requires T3+ tier
      {
        path: "analytics",
        element: (
          <TierGuard minTier={3} fallback={<AccessDenied message="Manager or higher access is required to view the Analytics Dashboard." />}>
            <AnalyticsDashboardPage />
          </TierGuard>
        ),
      },
      // Behavioral Dashboard (REQ-010 Phase 4) - Requires T3+ tier
      {
        path: "behavioral",
        element: (
          <TierGuard minTier={3} fallback={<AccessDenied message="Manager or higher access is required to view the Behavioral Dashboard." />}>
            <BehavioralDashboardPage />
          </TierGuard>
        ),
      },
      // Help Center (REQ-043: Help Articles Portal)
      {
        path: "help",
        children: [
          {
            index: true,
            element: <HelpIndexPage />,
          },
          {
            path: "contact",
            element: <HelpContactPage />,
          },
          {
            path: ":slug",
            element: <HelpArticlePage />,
          },
        ],
      },
      // Account Settings (REQ-029 Phase 2)
      {
        path: "account",
        children: [
          {
            index: true,
            element: <AccountIndexPage />,
          },
          {
            path: "profile",
            element: <AccountProfilePage />,
          },
          {
            path: "security",
            element: <AccountSecurityPage />,
          },
          {
            path: "notifications",
            element: <AccountNotificationsPage />,
          },
          {
            path: "sessions",
            element: <AccountSessionsPage />,
          },
        ],
      },
    ],
  },

  // Error routes
  {
    path: "errors",
    children: [
      {
        path: "not-authorized",
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <NotAuthorizedPage />
          </React.Suspense>
        ),
      },
      {
        path: "not-found",
        element: (
          <React.Suspense fallback={<LoadingFallback />}>
            <NotFoundPage />
          </React.Suspense>
        ),
      },
    ],
  },

  // 404 catch-all
  {
    path: "*",
    element: (
      <React.Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </React.Suspense>
    ),
  },
];
